import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Crown, Check, MessageSquare, Paperclip, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/common";
import { useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";

const PERKS = [
  { icon: MessageSquare, t: "Send private messages", d: "Write to any technician or company on WindWorkHub." },
  { icon: Paperclip, t: "Photos & PDF attachments", d: "Share certificates, CVs, site photos and documents in chat." },
  { icon: Star, t: "VIP badge on your profile", d: "Stand out in search results and candidate lists." },
];

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

export default function Vip() {
  const api = useApi();
  const { refresh } = useMe();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api("get", "/billing").then(setInfo);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = async () => {
    setBusy(true);
    try { const r = await api("post", "/billing/checkout", { origin_url: window.location.origin }); window.location.href = r.checkout_url; }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const toggleCancel = async (cancel) => {
    if (cancel && !window.confirm("Cancel your VIP subscription? You keep VIP until the end of the paid period.")) return;
    setBusy(true);
    try { await api("post", cancel ? "/billing/cancel" : "/billing/resume"); toast.success(cancel ? "Subscription will end at period end" : "Subscription resumed"); await load(); refresh(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!info) return <Loader />;
  const sub = info.subscription;
  const activeSub = sub && ["active", "trialing", "past_due"].includes(sub.status);

  return (
    <div className="max-w-4xl">
      <p className="eyebrow">Membership</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3">WindWorkHub VIP <Crown className="text-amber-500" /></h1>
      <div className="mt-8 grid md:grid-cols-5 gap-4">
        <div className="md:col-span-3 panel p-6 space-y-5">
          {PERKS.map((p) => (
            <div key={p.t} className="flex gap-4">
              <span className="w-10 h-10 rounded bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0"><p.icon size={18} className="text-amber-600" /></span>
              <div><p className="font-bold">{p.t}</p><p className="text-sm text-slate-600">{p.d}</p></div>
            </div>
          ))}
        </div>
        <div className="md:col-span-2 relative overflow-hidden rounded-md bg-slate-950 text-white p-6 flex flex-col grain" data-testid="vip-status-card">
          <p className="eyebrow !text-amber-400">Your status</p>
          {info.is_vip ? (
            <>
              <p className="mt-3 text-2xl font-extrabold flex items-center gap-2" data-testid="vip-status-active"><Check className="text-emerald-400" />VIP active</p>
              <p className="mt-1 text-sm text-white/70" data-testid="vip-until">
                {info.is_admin ? "Admins always have VIP." : info.vip_source === "trial" ? `Free launch period until ${fmt(info.vip_until)}` : `Valid until ${fmt(info.vip_until)}`}
              </p>
            </>
          ) : <p className="mt-3 text-2xl font-extrabold" data-testid="vip-status-inactive">Not VIP</p>}
          <div className="mt-6 pt-6 border-t border-white/10">
            <p className="text-4xl font-black" data-testid="vip-price">{info.price_sek} <span className="text-base font-semibold text-white/60">SEK / month</span></p>
            <p className="text-xs text-white/50 mt-1">Cancel anytime. Taxes calculated at checkout.</p>
          </div>
          <div className="mt-6">
            {activeSub ? (
              sub.cancel_at_period_end
                ? <Button className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400" disabled={busy} onClick={() => toggleCancel(false)} data-testid="vip-resume-button">Resume subscription</Button>
                : <Button variant="outline" className="w-full bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white" disabled={busy} onClick={() => toggleCancel(true)} data-testid="vip-cancel-button">Cancel subscription</Button>
            ) : !info.is_admin && (
              <Button className="w-full h-11 bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold" disabled={busy} onClick={subscribe} data-testid="vip-subscribe-button">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <Crown size={16} />}{info.is_vip ? "Subscribe now" : "Become VIP"}
              </Button>
            )}
            {activeSub && <p className="mt-2 text-xs text-white/60" data-testid="vip-sub-status">Subscription: {sub.status}{sub.cancel_at_period_end ? " · ends at period end" : " · renews monthly"}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function PaymentResult() {
  const [params] = useSearchParams();
  const api = useApi();
  const { refresh } = useMe();
  const sid = params.get("session_id");
  const [state, setState] = useState(sid ? "checking" : "cancel");

  useEffect(() => {
    if (!sid) return;
    let n = 0; let off = false;
    const poll = async () => {
      try {
        const r = await api("get", `/payments/status/${sid}`);
        if (off) return;
        if (r.payment_status === "paid") { setState("paid"); refresh(); return; }
        if (["expired", "failed"].includes(r.payment_status)) { setState("failed"); return; }
      } catch (err) { console.error("Payment status check failed:", err); }
      if (++n < 15) setTimeout(poll, 2000); else setState("timeout");
    };
    poll();
    return () => { off = true; };
  }, [sid, api, refresh]);

  const text = {
    checking: ["Confirming your payment...", "This takes a few seconds."],
    paid: ["Welcome to VIP!", "Your membership is active. You can now send messages and attachments."],
    failed: ["Payment failed", "No money was taken. You can try again."],
    timeout: ["Still processing", "Check your VIP page in a minute."],
    cancel: ["Payment cancelled", "Nothing was charged."],
  }[state];
  return (
    <div className="max-w-lg mx-auto panel p-8 text-center mt-10" data-testid={`payment-result-${state}`}>
      {state === "checking" ? <Loader2 className="mx-auto animate-spin text-sky-600" size={36} /> : <Crown className={`mx-auto ${state === "paid" ? "text-amber-500" : "text-slate-300"}`} size={36} />}
      <h1 className="mt-4 text-2xl font-extrabold">{text[0]}</h1>
      <p className="mt-2 text-slate-600">{text[1]}</p>
      <div className="mt-6 flex justify-center gap-2">
        <Button asChild data-testid="payment-go-vip"><Link to="/vip">VIP page</Link></Button>
        <Button asChild variant="outline" data-testid="payment-go-messages"><Link to="/messages">Messages</Link></Button>
      </div>
    </div>
  );
}
